import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import Typography from 'kingpin/atoms/Typography';

import ToastContext, { ToastPlacement } from '../../../contexts/ToastContext';
import { Z_INDEX } from '../../../constants';

const ToastMessage = styled(motion.div)<{ $placement: ToastPlacement }>`
  position: absolute;
  width: fit-content;
  top: 0px;
  /* Centred whatever its width */
  left: 0;
  right: 0;
  margin: auto;
  background-color: var(--color-grey-2);
  padding: 8px 32px;

  text-align: center;
  border-radius: 8px;

  box-shadow:
    0px 0px 0px 1px rgba(87, 89, 97, 0.1),
    1px 7px 20px -2px rgba(0, 0, 0, 0.4),
    0px -1px 3px -2px rgba(0, 0, 0, 0.25);

  z-index: ${Z_INDEX.TOAST};

  ${(props) =>
    props.$placement === 'bottom-right' &&
    css`
      position: fixed;
      top: auto;
      left: auto;
      right: ${BOTTOM_RIGHT_INSET}px;
      bottom: 0;
      margin: 0;
    `}
`;

// The toast's button, in the message's colour
const ActionButton = styled.button`
  padding: 0 4px;
  border: none;
  border-radius: 4px;
  background: none;
  color: var(--color-bg-static-white);
  font-family: Inter;
  font-size: 15px;
  font-weight: 600;
  line-height: 24px;
  text-decoration: underline;
  cursor: pointer;
`;

const DEFAULT_TOP = 50;
const BOTTOM_RIGHT_INSET = 24;
const DURATION_MS = 3000;
// Long enough to reach the button
const ACTION_DURATION_MS = 6000;

type ToastAction = { label: string; onAction: () => void };

// Slides in from the edge it sits on
const variants = {
  opened: (custom: { top: number; placement: ToastPlacement }) =>
    custom.placement === 'bottom-right'
      ? { opacity: 1, bottom: BOTTOM_RIGHT_INSET }
      : { opacity: 1, top: custom.top },
  closed: (custom: { top: number; placement: ToastPlacement }) =>
    custom.placement === 'bottom-right'
      ? { opacity: 0, bottom: 0 }
      : { opacity: 0, top: 0 },
};

const ToastProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [top, setTop] = useState<number>(DEFAULT_TOP);
  const [action, setAction] = useState<ToastAction | undefined>();
  // Kept as it hides, so it leaves the way it came in
  const [placement, setPlacement] = useState<ToastPlacement>('top');

  const hideToast = useCallback(() => {
    setIsVisible(false);
    setMessage('');
    setTop(DEFAULT_TOP);
    setAction(undefined);
  }, []);

  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current !== undefined) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const show = useCallback(
    ({
      newMessage,
      customTop,
      newAction,
      newPlacement = 'top',
      durationMs,
    }: {
      newMessage: string;
      customTop?: number;
      newAction?: ToastAction;
      newPlacement?: ToastPlacement;
      durationMs: number;
    }) => {
      if (toastTimeoutRef.current !== undefined) {
        clearTimeout(toastTimeoutRef.current);
      }
      setMessage(newMessage);
      if (customTop !== undefined) {
        setTop(customTop);
      }
      setAction(newAction);
      setPlacement(newPlacement);
      setIsVisible(true);

      toastTimeoutRef.current = setTimeout(() => {
        hideToast();
      }, durationMs);
    },
    [hideToast],
  );

  const showToast = useCallback(
    (newMessage: string, customTop?: number) => {
      show({ newMessage, customTop, durationMs: DURATION_MS });
    },
    [show],
  );

  const showActionToast = useCallback(
    ({
      message: newMessage,
      actionLabel,
      onAction,
      placement: newPlacement,
    }: {
      message: string;
      actionLabel: string;
      onAction: () => void;
      placement?: ToastPlacement;
    }) => {
      show({
        newMessage,
        newAction: { label: actionLabel, onAction },
        newPlacement,
        durationMs: ACTION_DURATION_MS,
      });
    },
    [show],
  );

  const onActionClick = useCallback(() => {
    if (!action) {
      return;
    }
    if (toastTimeoutRef.current !== undefined) {
      clearTimeout(toastTimeoutRef.current);
    }
    hideToast();
    action.onAction();
  }, [action, hideToast]);

  return (
    <ToastContext.Provider
      value={{
        showToast,
        showActionToast,
      }}
    >
      {children}
      <ToastMessage
        // Remounted for a new placement: the edge it animated from last time
        // (an inline top or bottom) would otherwise pin it to both
        key={placement}
        $placement={placement}
        variants={variants}
        initial="closed"
        animate={isVisible ? 'opened' : 'closed'}
        custom={{ top, placement }}
        // Hidden, it doesn't catch clicks meant for what's under it
        style={{ pointerEvents: isVisible ? 'auto' : 'none' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Typography.Header type="H4" color="white">
            {message}
          </Typography.Header>
          {action && (
            <ActionButton
              type="button"
              onClick={onActionClick}
              data-testid="toast-action"
            >
              {action.label}
            </ActionButton>
          )}
        </div>
      </ToastMessage>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
